/**
 * The single source of truth for the high-lift foil's screen frame (FXC-9009).
 *
 * Both the 2-D foil canvas (`FoilGeometryCanvas`, drawn with a letterboxed
 * world→pixel transform) and the LIC flow layer (`FlowVisLayer`, drawn with a
 * three.js orthographic camera) must map chord coordinates to the SAME pixels, or
 * the field and the geometry drift apart. They draw with different machinery, so
 * the only safe way to keep them aligned is to derive both from one constant + one
 * letterbox helper here — `viewCameraRect` is provably pixel-identical to the
 * 2-D `world()` transform (see foilView.test.ts).
 */

export interface ViewRect {
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
}

/** The foil viewport world rect (chords). Matches the vendored webui `VIEW` constant. */
export const FOIL_VIEW: ViewRect = { xmin: -0.5, xmax: 1.5, ymin: -0.6, ymax: 0.4 };

/**
 * Letterbox fit of `view` into a `w×h` buffer: a single uniform scale `s` (so the
 * geometry isn't squashed) with centring offsets `ox/oy` (the margin on the axis
 * that isn't the binding constraint). The 2-D canvas and the ortho camera both
 * start from this.
 */
export function letterbox(view: ViewRect, w: number, h: number): { s: number; ox: number; oy: number } {
  const s = Math.min(w / (view.xmax - view.xmin), h / (view.ymax - view.ymin));
  const ox = (w - s * (view.xmax - view.xmin)) / 2;
  const oy = (h - s * (view.ymax - view.ymin)) / 2;
  return { s, ox, oy };
}

/**
 * The orthographic-camera world rect `{left,right,bottom,top}` whose mapping to a
 * `w×h` buffer is pixel-identical to the letterboxed 2-D `world()` transform. An
 * ortho camera maps its rect linearly onto the FULL buffer, so to reproduce the
 * letterbox we widen the rect past `view` by exactly the margin the letterbox would
 * have left blank: `left = xmin − ox/s`, then span the full `w/s` (and `h/s`) of
 * world units the buffer covers. y is up here (camera.top ↔ the top row), matching
 * the 2-D transform's `h − …` flip.
 */
export function viewCameraRect(
  view: ViewRect,
  w: number,
  h: number,
): { left: number; right: number; bottom: number; top: number } {
  const { s, ox, oy } = letterbox(view, w, h);
  const left = view.xmin - ox / s;
  const bottom = view.ymin - oy / s;
  return { left, right: left + w / s, bottom, top: bottom + h / s };
}

/**
 * Zoom `view` by `factor` about a fixed world anchor `(ax, az)` — the point under
 * the cursor stays put, so wheel-zoom feels anchored where you point. `factor < 1`
 * zooms in (shrinks the rect), `factor > 1` zooms out.
 */
export function zoomViewAbout(view: ViewRect, factor: number, ax: number, az: number): ViewRect {
  return {
    xmin: ax - (ax - view.xmin) * factor,
    xmax: ax + (view.xmax - ax) * factor,
    ymin: az - (az - view.ymin) * factor,
    ymax: az + (view.ymax - az) * factor,
  };
}

/** Linear zoom level of `view` relative to `base` (1 = base; >1 = zoomed in). */
export function zoomLevel(view: ViewRect, base: ViewRect = FOIL_VIEW): number {
  return (base.xmax - base.xmin) / (view.xmax - view.xmin);
}

/** True when `view` is (near-)exactly the home `base` rect — i.e. not zoomed or panned. */
export function isHomeView(view: ViewRect, base: ViewRect = FOIL_VIEW, eps = 1e-6): boolean {
  return (
    Math.abs(view.xmin - base.xmin) < eps &&
    Math.abs(view.xmax - base.xmax) < eps &&
    Math.abs(view.ymin - base.ymin) < eps &&
    Math.abs(view.ymax - base.ymax) < eps
  );
}

/**
 * The view rect that frames the given world-space point groups (e.g. the deployed
 * main / vane / flaperon contours) with a fractional margin — the "Fill view"
 * control re-centres + frames the foil no matter how far it's been deployed,
 * dragged, or zoomed. `minSpan` stops a tiny/degenerate foil from over-zooming.
 * Falls back to `FOIL_VIEW` when there are no finite points.
 */
export function framePoints(
  groups: ReadonlyArray<ReadonlyArray<readonly [number, number]>>,
  padFrac = 0.15,
  minSpan = 0.6,
): ViewRect {
  let xmin = Infinity;
  let xmax = -Infinity;
  let ymin = Infinity;
  let ymax = -Infinity;
  for (const g of groups) {
    for (const p of g) {
      const x = p[0];
      const y = p[1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < xmin) xmin = x;
      if (x > xmax) xmax = x;
      if (y < ymin) ymin = y;
      if (y > ymax) ymax = y;
    }
  }
  if (!Number.isFinite(xmin) || !Number.isFinite(ymin)) return { ...FOIL_VIEW };
  const cx = (xmin + xmax) / 2;
  const cy = (ymin + ymax) / 2;
  const w = Math.max(xmax - xmin, minSpan);
  const h = Math.max(ymax - ymin, minSpan);
  const padX = w * padFrac;
  const padY = h * padFrac;
  return {
    xmin: cx - w / 2 - padX,
    xmax: cx + w / 2 + padX,
    ymin: cy - h / 2 - padY,
    ymax: cy + h / 2 + padY,
  };
}
